import { parseISO } from "date-fns";
import { z } from "zod";

export const AppointmentNoticeSchema = z.number().int().min(0).max(2147483647);
const RevisionSchema = z.number().int().positive().max(Number.MAX_SAFE_INTEGER);

export const AppointmentRulesSchema = z.object({
  businessId: z.string(),
  customerCancellationEnabled: z.boolean(),
  cancellationNoticeMinutes: AppointmentNoticeSchema,
  customerReschedulingEnabled: z.boolean(),
  reschedulingNoticeMinutes: AppointmentNoticeSchema,
  minimumBookingNoticeMinutes: AppointmentNoticeSchema,
  revision: RevisionSchema,
  createdAt: z.date(),
  modifiedAt: z.date().nullable(),
});

const UpdateAppointmentRulesRequestSchema = AppointmentRulesSchema.omit({
  businessId: true, revision: true, createdAt: true, modifiedAt: true,
}).extend({ expectedRevision: RevisionSchema });

const AppointmentRulesDtoSchema = z.object({
  business_id: z.string(),
  customer_cancellation_enabled: z.boolean(),
  cancellation_notice_minutes: AppointmentNoticeSchema,
  customer_rescheduling_enabled: z.boolean(),
  rescheduling_notice_minutes: AppointmentNoticeSchema,
  minimum_booking_notice_minutes: AppointmentNoticeSchema,
  revision: RevisionSchema,
  created_at: z.string(),
  modified_at: z.string().nullable(),
});

export const UpdateAppointmentRulesDtoSchema = AppointmentRulesDtoSchema.omit({
  business_id: true, revision: true, created_at: true, modified_at: true,
}).extend({ expected_revision: RevisionSchema });

export type AppointmentRulesDto = z.infer<typeof AppointmentRulesDtoSchema>;
export type UpdateAppointmentRulesDto = z.infer<typeof UpdateAppointmentRulesDtoSchema>;
export type AppointmentRules = z.infer<typeof AppointmentRulesSchema>;
export type UpdateAppointmentRulesRequest = z.infer<typeof UpdateAppointmentRulesRequestSchema>;

export function fromAppointmentRulesDtoToAppointmentRules(dto: AppointmentRulesDto): AppointmentRules {
  const data = AppointmentRulesDtoSchema.parse(dto);
  return {
    businessId: data.business_id,
    customerCancellationEnabled: data.customer_cancellation_enabled,
    cancellationNoticeMinutes: data.cancellation_notice_minutes,
    customerReschedulingEnabled: data.customer_rescheduling_enabled,
    reschedulingNoticeMinutes: data.rescheduling_notice_minutes,
    minimumBookingNoticeMinutes: data.minimum_booking_notice_minutes,
    revision: data.revision,
    createdAt: parseISO(data.created_at),
    modifiedAt: data.modified_at ? parseISO(data.modified_at) : null,
  };
}

export function fromUpdateAppointmentRulesRequestToDto(request: UpdateAppointmentRulesRequest): UpdateAppointmentRulesDto {
  const data = UpdateAppointmentRulesRequestSchema.parse(request);
  return {
    expected_revision: data.expectedRevision,
    customer_cancellation_enabled: data.customerCancellationEnabled,
    cancellation_notice_minutes: data.cancellationNoticeMinutes,
    customer_rescheduling_enabled: data.customerReschedulingEnabled,
    rescheduling_notice_minutes: data.reschedulingNoticeMinutes,
    minimum_booking_notice_minutes: data.minimumBookingNoticeMinutes,
  };
}
